/**
 * The References panel's decisions, as pure functions.
 * (Olivia, SPEC-references §5–6.)
 *
 * Problems are split by nature, not severity. A dangling \ref is a typo in a
 * sentence and ships as ?? — it is a problem. A duplicate is bookkeeping
 * LaTeX resolves wrongly and silently — a problem. An unused label is a draft
 * not yet written, and must not accrue a badge: it goes in a collapsed *Also*
 * group, uncoloured, and never in Problems.
 */

export interface ReportTarget {
  key: string;
  kind: string;
  blockId: string;
  caption: string | null;
  number: string | null;
  page: number | null;
}

export interface ReportUse {
  key: string;
  blockId: string;
  form: string;
}

export interface ReportProblem {
  kind: "dangling" | "duplicate" | "unused" | string;
  key: string;
  blockIds: string[];
}

export interface ReferenceReport {
  numbered: boolean;
  numberedAt: string | null;
  targets: ReportTarget[];
  uses: ReportUse[];
  problems: ReportProblem[];
}

/**
 * The report as the API actually sends it, made into the shape above.
 *
 * The API's serializer omits null fields: an uncompiled target arrives with
 * no `number` key at all, and a report with no numbers has no `numberedAt`.
 * Every consumer then reads `undefined`, and a check written as
 * `number === null` quietly says "numbered" — the footer counted 0 targets
 * added since, against the real API, while every fixture with an explicit
 * null passed. Normalising here, once, is what keeps the rest honest.
 */
export function normalizeReport(raw: unknown): ReferenceReport {
  const r = (raw ?? {}) as Partial<ReferenceReport> & Record<string, unknown>;
  const list = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  return {
    numbered: Boolean(r.numbered),
    numberedAt: typeof r.numberedAt === "string" ? r.numberedAt : null,
    targets: list<Partial<ReportTarget>>(r.targets).map((t) => ({
      key: String(t.key ?? ""),
      kind: String(t.kind ?? ""),
      blockId: String(t.blockId ?? ""),
      caption: typeof t.caption === "string" ? t.caption : null,
      number: typeof t.number === "string" ? t.number : null,
      page: typeof t.page === "number" ? t.page : null,
    })),
    uses: list<ReportUse>(r.uses),
    problems: list<Partial<ReportProblem>>(r.problems).map((p) => ({
      kind: String(p.kind ?? ""),
      key: String(p.key ?? ""),
      blockIds: list<string>(p.blockIds),
    })),
  };
}

export interface TargetRow extends ReportTarget {
  uses: number;
}

export interface PanelModel {
  /** Dangling and duplicate only. Empty means the section is absent. */
  problems: ReportProblem[];
  /** Targets something points at, in document order. */
  targets: TargetRow[];
  /** Targets nothing points at yet — the collapsed "Also" group. */
  unused: TargetRow[];
  footer: string | null;
}

/**
 * Group the report into the panel's three sections.
 *
 * A target appears once: under *In this document* when anything points at it,
 * under *Also* when nothing does. Showing an unused label in both places would
 * give the weakest signal the same weight as the strongest.
 */
export function buildPanel(report: ReferenceReport, now: Date = new Date()): PanelModel {
  const useCount = new Map<string, number>();
  for (const u of report.uses) useCount.set(u.key, (useCount.get(u.key) ?? 0) + 1);

  const rows: TargetRow[] = report.targets.map((t) => ({ ...t, uses: useCount.get(t.key) ?? 0 }));

  return {
    problems: report.problems.filter((p) => p.kind === "dangling" || p.kind === "duplicate"),
    targets: rows.filter((r) => r.uses > 0),
    unused: rows.filter((r) => r.uses === 0),
    footer: footerFor(report, now),
  };
}

/**
 * Said when a document has labels but no compile has numbered them yet.
 *
 * For a day this said "The quick preview doesn't number references. Compile a
 * PDF to number them." — true while only pdflatex numbered anything. The Typst
 * preview now numbers references and keeps the numbers (lilia-editor-api
 * #150, #151), so, as Olivia specified, it went back to plain "compile".
 */
export const NOT_NUMBERED_YET = "Not numbered yet. Compile to number them.";

/**
 * The one line that dates the numbers — "Numbers from the last compile ·
 * today, 14:06 · 1 target added since". It does the work a stale badge on
 * every row would do, and it is the only place the tool mentions compiling.
 *
 * Any compile — the quick preview or the PDF — numbers the labels and keeps
 * the numbers. With labels and no numbers it says so (NOT_NUMBERED_YET); with no
 * labels at all there is nothing to number and no footer.
 */
export function footerFor(report: ReferenceReport, now: Date = new Date()): string | null {
  const at = report.numberedAt ? new Date(report.numberedAt) : null;
  if (!at || Number.isNaN(at.getTime())) return report.targets.length > 0 ? NOT_NUMBERED_YET : null;

  const added = report.targets.filter((t) => t.number === null).length;
  const parts = ["Numbers from the last compile", when(at, now)];
  if (added > 0) parts.push(`${added} target${added === 1 ? "" : "s"} added since`);
  return parts.join(" · ");
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "today, 14:06" · "yesterday, 09:12" · "3 Sep, 17:40". Local time.
 *
 * Formatted by hand, not with toLocaleDateString: the ICU data behind it
 * differs between runtimes — this one writes "Sept", others "Sep" — so the
 * footer would read differently depending on the reader's browser.
 */
export function when(at: Date, now: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const hhmm = `${pad(at.getHours())}:${pad(at.getMinutes())}`;
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((day(now) - day(at)) / 86_400_000);
  if (diff === 0) return `today, ${hhmm}`;
  if (diff === 1) return `yesterday, ${hhmm}`;
  return `${at.getDate()} ${MONTHS[at.getMonth()]}, ${hhmm}`;
}

/** The label a row shows: the caption, or the key when there is none. */
export function rowLabel(t: ReportTarget): string {
  return t.caption?.trim() || t.key;
}

/** How a problem reads in the rail. Dangling says what ships, not what is wrong. */
export function problemText(p: ReportProblem): string {
  if (p.kind === "dangling") return `Ships as ?? — nothing is labelled ${p.key}`;
  if (p.kind === "duplicate") return `${p.key} is used ${p.blockIds.length} times — LaTeX keeps only the last`;
  return p.key;
}
